import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AdminAlert } from "../components/AdminAlert";
import { useApi } from "../hooks/useApi";
import { ApiError, api } from "../lib/api";

export function AdminUsers(): JSX.Element {
  const { t } = useTranslation();
  const [refresh, setRefresh] = useState(0);
  const data = useApi(() => api.admin.users(), [refresh]);
  const [error, setError] = useState<string | null>(null);
  const reload = (): void => setRefresh((r) => r + 1);

  if (data.status === "loading") {
    return <p className="launchpad__hint">{t("admin.loading")}</p>;
  }
  if (data.status === "error") {
    const msg = data.error instanceof ApiError ? data.error.message : data.error.message;
    return <p className="launchpad__hint launchpad__hint--error">{msg}</p>;
  }

  const onPromote = async (user_id: string): Promise<void> => {
    setError(null);
    try {
      await api.admin.promote(user_id);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const onRevoke = async (user_id: string): Promise<void> => {
    if (!window.confirm(t("admin.users.confirmRevoke"))) return;
    setError(null);
    try {
      await api.admin.revoke(user_id);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const fmtSeen = (iso: string | null): string =>
    iso ? new Date(iso).toLocaleString() : t("admin.users.neverSeen");

  return (
    <section className="admin">
      <h2>{t("admin.usersTitle")}</h2>
      {error ? <AdminAlert kind="error">{error}</AdminAlert> : null}

      <h3>{t("admin.users.adminsHeading")}</h3>
      <table className="admin__table">
        <thead>
          <tr>
            <th>{t("admin.users.col.user")}</th>
            <th>{t("admin.users.col.email")}</th>
            <th>{t("admin.users.col.source")}</th>
            <th>{t("admin.users.col.lastSeen")}</th>
            <th>{t("admin.users.col.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {data.data.admins.map((a) => (
            <tr key={`${a.source}-${a.user_id ?? a.email}`}>
              <td>{a.display_name ?? a.user_id ?? a.email ?? "—"}</td>
              <td>{a.email ?? "—"}</td>
              <td>
                <span className="badge">
                  {a.source === "bootstrap"
                    ? t("admin.users.source.bootstrap")
                    : t("admin.users.source.granted")}
                </span>
              </td>
              <td>{fmtSeen(a.last_seen_at)}</td>
              <td className="admin__actions">
                {a.can_revoke && a.user_id ? (
                  <button type="button" onClick={() => void onRevoke(a.user_id as string)}>
                    {t("admin.users.revoke")}
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
          {data.data.admins.length === 0 ? (
            <tr>
              <td colSpan={5} className="admin__empty">
                {t("admin.users.noAdmins")}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <h3>{t("admin.users.promotableHeading")}</h3>
      <table className="admin__table">
        <thead>
          <tr>
            <th>{t("admin.users.col.user")}</th>
            <th>{t("admin.users.col.email")}</th>
            <th>{t("admin.users.col.lastSeen")}</th>
            <th>{t("admin.users.col.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {data.data.promotable.map((p) => (
            <tr key={p.user_id}>
              <td>{p.display_name ?? p.user_id}</td>
              <td>{p.email ?? "—"}</td>
              <td>{fmtSeen(p.last_seen_at)}</td>
              <td className="admin__actions">
                <button type="button" onClick={() => void onPromote(p.user_id)}>
                  {t("admin.users.promote")}
                </button>
              </td>
            </tr>
          ))}
          {data.data.promotable.length === 0 ? (
            <tr>
              <td colSpan={4} className="admin__empty">
                {t("admin.users.noPromotable")}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </section>
  );
}
